import React, { useState } from 'react';
import { Info, Code, Calendar } from 'lucide-react';
import { cn } from '@/utils/cn';

export interface VersionProps {
  version: string;
  buildNumber?: string;
  environment?: 'development' | 'staging' | 'production';
  buildDate?: string;
  className?: string;
  showDetails?: boolean;
}

const environmentConfig = {
  development: {
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    label: 'DEV',
  },
  staging: {
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-100',
    label: 'STAGING',
  },
  production: {
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    label: 'PROD',
  },
};

export const Version: React.FC<VersionProps> = ({
  version,
  buildNumber,
  environment = 'development',
  buildDate,
  className,
  showDetails = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const envConfig = environmentConfig[environment];

  const toggleDetails = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {/* Environment Badge */}
      <span
        className={cn(
          'px-2 py-1 text-xs font-medium rounded-full',
          envConfig.color,
          envConfig.bgColor
        )}
      >
        {envConfig.label}
      </span>

      {/* Version Info */}
      <div className="flex items-center gap-1">
        <span className="text-sm text-gray-600">v{version}</span>

        {(buildNumber || buildDate || showDetails) && (
          <button
            onClick={toggleDetails}
            className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors duration-200"
            title="Show version details"
          >
            <Info className="w-3 h-3" />
          </button>
        )}
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="absolute bottom-full right-0 mb-2 p-3 bg-white rounded-lg shadow-lg border border-gray-200 min-w-48">
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <Code className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600">Version:</span>
              <span className="font-mono text-gray-900">{version}</span>
            </div>

            {buildNumber && (
              <div className="flex items-center gap-2">
                <Info className="w-4 h-4 text-gray-400" />
                <span className="text-gray-600">Build:</span>
                <span className="font-mono text-gray-900">{buildNumber}</span>
              </div>
            )}

            {buildDate && (
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-400" />
                <span className="text-gray-600">Built:</span>
                <span className="text-gray-900">{buildDate}</span>
              </div>
            )}

            <div className="flex items-center gap-2">
              <div className={cn('w-2 h-2 rounded-full', envConfig.bgColor)} />
              <span className="text-gray-600">Environment:</span>
              <span className={cn('font-medium', envConfig.color)}>
                {environment.charAt(0).toUpperCase() + environment.slice(1)}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Version;
